'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useMemo, useState } from 'react';
import type {
  MobileFrontEndBubbleMessageView,
  MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { isFocusedMessage } from '@/shared/mobile-service/lib/citation-focus';
import { ARTIFACT_ID_FIELD } from '@/shared/mobile-service/lib/constants';
import { artifactDisplayName } from '@/shared/mobile-service/lib/labels';
import { groupHydratedMessageRows, type MappedMessage } from '@/shared/mobile-service/lib/message-mapping';
import { MessageBubbles } from './MessageBubbles';
import { MessageMarkdownPane } from './MessageMarkdownPane';
import { ThreadList } from './ThreadList';

/**
 * Citation-pane thread: the hydrated conversation as bubbles, not a new viewer tab.
 *
 * Reuses the viewer's bubble list and thread rail. Unlike {@link MessageThreadView}
 * this has no viewer context and no GraphQL of its own — hydrate already returned
 * the cited rows and the registry view says how to read them.
 */
export function CitedThread({
  rows,
  view,
  artifact,
  focusIds,
  highlightIds = [],
  viewerHref,
  label,
  mobileAcquisitionId = null,
}: {
  rows: Record<string, unknown>[];
  view: MobileFrontEndBubbleMessageView;
  artifact: MobileUiConfigArtifact;
  focusIds: Array<string | number>;
  highlightIds?: Array<string | number>;
  viewerHref: string | null;
  label?: string | null;
  mobileAcquisitionId?: number | null;
}) {
  const t = useTimePilotT();
  const [selectedMessage, setSelectedMessage] = useState<MappedMessage | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [userPickedThread, setUserPickedThread] = useState(false);

  const groups = useMemo(
    () => groupHydratedMessageRows(rows, view, ARTIFACT_ID_FIELD),
    [rows, view],
  );
  const defaultKey = useMemo(() => {
    const focused = groups.find((group) =>
      group.messages.some((message) => isFocusedMessage(message, focusIds)),
    );
    return focused?.thread.key ?? groups[0]?.thread.key ?? null;
  }, [groups, focusIds]);

  const rowIdentity = rows
    .map((row) => String(row[ARTIFACT_ID_FIELD] ?? ''))
    .join(',');
  useEffect(() => {
    setUserPickedThread(false);
    setSelectedKey(null);
  }, [rowIdentity]);

  const activeKey =
    userPickedThread && groups.some((group) => group.thread.key === selectedKey)
      ? selectedKey
      : defaultKey;
  const selected = groups.find((group) => group.thread.key === activeKey) ?? groups[0];
  const messages = selected?.messages ?? [];

  const participants = selected?.thread.title?.trim() || '';
  const sourceLabel = label?.trim() || null;
  const title = participants || sourceLabel || artifactDisplayName(artifact, view.title);
  const subtitle = sourceLabel && sourceLabel !== title ? sourceLabel : null;
  // The badge is the bubbles this tab renders. The acquisition-wide total
  // belongs on the viewer rail, where opening a thread loads every message.
  const threads = groups.map((group) => ({
    ...group.thread,
    count: group.messages.length,
  }));
  const multiple = groups.length > 1;

  const bubbles = (
    <MessageBubbles
      messages={messages}
      threadSelected={activeKey != null || groups.length === 1}
      isLoading={false}
      error={null}
      focusIds={focusIds}
      highlightIds={highlightIds}
      onRetry={() => undefined}
      onSelectMessage={setSelectedMessage}
    />
  );

  const viewerLink = viewerHref ? (
    <a
      href={viewerHref}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0 text-xs text-white/80 underline decoration-white/40 underline-offset-2 hover:text-white"
    >{t("viewers.openInMobileViewer")}</a>
  ) : null;

  const popover = selectedMessage ? (
    <MessageMarkdownPane
      message={selectedMessage}
      artifact={artifact}
      mobileAcquisitionId={mobileAcquisitionId}
      title={`${artifactDisplayName(artifact, view.title)} message`}
      onClose={() => setSelectedMessage(null)}
    />
  ) : null;

  if (multiple) {
    return (
      <div
        data-testid="CitationCitedThread"
        className="flex min-h-0 w-full flex-1 overflow-hidden rounded-md bg-white/5"
      >
        <aside
          data-testid="CitationCitedThreadRail"
          className="flex w-56 shrink-0 flex-col overflow-y-auto border-r border-white/10"
        >
          <ThreadList
            threads={threads}
            selectedKey={activeKey}
            isLoading={false}
            error={null}
            onRetry={() => undefined}
            onSelect={(key) => {
              setUserPickedThread(true);
              setSelectedKey(key);
              setSelectedMessage(null);
            }}
          />
        </aside>
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-400/40 bg-amber-400/10 px-3 py-2">
            <div className="min-w-0">
              <p className="m-0 truncate text-sm text-amber-100">{title}</p>
              {subtitle ? <p className="m-0 text-xs text-amber-100/70">{subtitle}</p> : null}
            </div>
            {viewerLink}
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto">{bubbles}</div>
        </section>
        {popover}
      </div>
    );
  }

  return (
    <div
      data-testid="CitationCitedThread"
      className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md bg-white/5"
    >
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-400/40 bg-amber-400/10 px-3 py-2">
        <div className="min-w-0">
          <p className="m-0 truncate text-sm text-amber-100">{title}</p>
          {subtitle ? <p className="m-0 text-xs text-amber-100/70">{subtitle}</p> : null}
        </div>
        {viewerLink}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">{bubbles}</div>
      {popover}
    </div>
  );
}
